/**
 * Centralized error handling for API responses.
 * Provides consistent error messages across the application.
 */

/**
 * Format API error into user-friendly message
 * @param {Error} error - Axios error object
 * @returns {Object} - { message: string, details: string, status: number }
 */
export const formatApiError = (error) => {
  if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
    return {
      message: 'Network Error',
      details: 'Unable to connect to the server. Please check if the backend is running and try again.',
      status: 0,
      type: 'network'
    };
  }

  if (error.message?.includes('CORS') || error.message?.includes('Access-Control-Allow-Origin')) {
    return {
      message: 'CORS Error',
      details: 'Cross-origin request blocked. Please check backend CORS configuration.',
      status: 0,
      type: 'cors'
    };
  }

  if (error.response) {
    const status = error.response.status;
    const data = error.response.data;

    switch (status) {
      case 401:
        return {
          message: 'Authentication Failed',
          details: 'Your session has expired. Please log out and log in again.',
          status: 401,
          type: 'auth'
        };

      case 403:
        return {
          message: 'Access Denied',
          details: data?.detail || 'You don\'t have permission to access this resource.',
          status: 403,
          type: 'permission'
        };

      case 404:
        return {
          message: 'Not Found',
          details: data?.detail || 'The requested resource was not found.',
          status: 404,
          type: 'not_found'
        };

      case 422:
        if (Array.isArray(data?.detail)) {
          const validationErrors = data.detail.map(err =>
            `${err.loc?.join('.')}: ${err.msg}`
          ).join('; ');
          return {
            message: 'Validation Error',
            details: validationErrors || 'Invalid request parameters.',
            status: 422,
            type: 'validation'
          };
        }
        return {
          message: 'Validation Error',
          details: data?.detail || 'Invalid request parameters.',
          status: 422,
          type: 'validation'
        };

      case 429:
        return {
          message: 'Rate Limit Exceeded',
          details: data?.detail || 'Too many requests. Please wait a moment and try again.',
          status: 429,
          type: 'rate_limit'
        };

      case 500:
      case 502:
      case 503:
        return {
          message: 'Server Error',
          details: data?.detail || 'The server encountered an error. Please try again later.',
          status: status,
          type: 'server'
        };

      default:
        return {
          message: 'Request Failed',
          details: data?.detail || error.message || 'An unexpected error occurred.',
          status: status,
          type: 'unknown'
        };
    }
  }

  if (error.message?.includes('timeout')) {
    return {
      message: 'Request Timeout',
      details: 'The request took too long to complete. Please try again.',
      status: 0,
      type: 'timeout'
    };
  }

  return {
    message: 'Error',
    details: error.message || 'An unexpected error occurred.',
    status: 0,
    type: 'unknown'
  };
};

/**
 * Log error for debugging (only in development)
 */
export const logError = (error, context = '') => {
  if (process.env.NODE_ENV === 'development') {
    console.error(`[API Error${context ? ` - ${context}` : ''}]`, error);
    if (error.response) {
      console.error('Response:', error.response.data);
      console.error('Status:', error.response.status);
    }
  }
};
